import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { atlasRoutes } from "@/app/router/routes";
import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { toastModule } from "@/modules/toast";
import {
  ASSIGNABLE_ROLE_KINDS,
  SHARED_ITEM_INVITE_STATUSES,
  SHARED_ITEM_INVITE_STATUS_LABELS,
  SHARED_ITEM_KIND_LABELS,
  SHARED_ITEM_KIND_SELECTABLE,
} from "@/pages/utilities/shared-item-invites/shared/constants";
import { Button } from "@/components/button";
import { Badge, Box, Card, Flex, Text } from "@radix-ui/themes";
import { SharedItemInviteDetail } from "@/pages/utilities/shared-item-invites/shared/types";
import { usePrimarySpaceAccountId } from "@/pages/utilities/shared-item-invites/shared/use-primary-space-account-id";

const isoToLocalInputValue = (iso?: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  const pad = (value: number) => value.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const inputValueToIso = (value: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const computeDiff = (current: Record<string, unknown>, initial: Record<string, unknown>) => {
  const diff: Record<string, unknown> = {};
  Object.entries(current).forEach(([key, value]) => {
    if (initial[key] !== value) {
      diff[key] = value;
    }
  });
  return diff;
};

export const SharedItemInvitesViewPage = () => {
  const { inviteId } = useParams<{ inviteId: Uuid }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { primarySpaceAccountId, isLoading: isPrimaryLoading } = usePrimarySpaceAccountId();

  const [formState, setFormState] = useState({
    inviterSpaceAccountId: "",
    sharedItemId: "",
    sharedItemKind: "NOTE",
    inviteeEmail: "",
    role: "VIEWER",
    expiresAt: "",
    status: "PENDING",
    acceptedBySpaceAccountId: "",
    acceptedAt: "",
    isRevoked: false,
  });
  const [initialPayload, setInitialPayload] = useState<Record<string, unknown>>({
    inviter_space_account_id: "",
    shared_item_id: "",
    shared_item_kind: "NOTE",
    invitee_email: "",
    role: "VIEWER",
    expires_at: null,
    status: "PENDING",
    accepted_by_space_account_id: null,
    accepted_at: null,
    is_revoked: false,
  });

  const result = useQuery({
    queryKey: ["shared-item-invite", inviteId],
    enabled: Boolean(inviteId),
    queryFn: async () => {
      const response = await api.get("/internal/v1/shared-item-invites/{invite_id}", {
        params: { path: { invite_id: inviteId! } },
      });
      return response.data as SharedItemInviteDetail;
    },
  });

  const invite = result.data;

  useEffect(() => {
    if (invite) {
      const nextState = {
        inviterSpaceAccountId: invite.inviter_space_account_id,
        sharedItemId: invite.shared_item_id,
        sharedItemKind: invite.shared_item_kind,
        inviteeEmail: invite.invitee_email,
        role: invite.role,
        expiresAt: isoToLocalInputValue(invite.expires_at),
        status: invite.status,
        acceptedBySpaceAccountId: invite.accepted_by_space_account_id ?? "",
        acceptedAt: isoToLocalInputValue(invite.accepted_at),
        isRevoked: invite.is_revoked,
      };
      setFormState(nextState);
      setInitialPayload({
        inviter_space_account_id: invite.inviter_space_account_id,
        shared_item_id: invite.shared_item_id,
        shared_item_kind: invite.shared_item_kind,
        invitee_email: invite.invitee_email,
        role: invite.role,
        expires_at: invite.expires_at,
        status: invite.status,
        accepted_by_space_account_id: invite.accepted_by_space_account_id,
        accepted_at: invite.accepted_at,
        is_revoked: invite.is_revoked,
      });
    }
  }, [invite]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["shared-item-invite", inviteId] });
    queryClient.invalidateQueries({ queryKey: ["shared-item-invites"] });
  };

  const updateMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      return api.patch("/internal/v1/shared-item-invites/{invite_id}", {
        params: { path: { invite_id: inviteId! } },
        body,
      });
    },
    onSuccess: () => {
      toastModule.triggerToast({ content: "Invite updated." });
      invalidate();
    },
    onError: err => {
      const message = err instanceof Error ? err.message : "Failed to update invite";
      toastModule.triggerToast({ content: message });
    },
  });

  const markStatusMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async (body: Record<string, any>) => {
      return api.post("/internal/v1/shared-item-invites/{invite_id}/mark-status", {
        params: { path: { invite_id: inviteId! } },
        body: {
          status: body.status,
          accepted_by_space_account_id: body.accepted_by_space_account_id,
          accepted_at: body.accepted_at,
        },
      });
    },
    onSuccess: () => {
      toastModule.triggerToast({ content: "Status updated." });
      invalidate();
    },
    onError: err => {
      const message = err instanceof Error ? err.message : "Failed to update status";
      toastModule.triggerToast({ content: message });
    },
  });

  const markRevokedMutation = useMutation({
    mutationFn: async () => {
      return api.post("/internal/v1/shared-item-invites/{invite_id}/mark-revoked", {
        params: { path: { invite_id: inviteId! } },
      });
    },
    onSuccess: () => {
      toastModule.triggerToast({ content: "Invite marked revoked." });
      invalidate();
    },
    onError: err => {
      const message = err instanceof Error ? err.message : "Failed to revoke invite";
      toastModule.triggerToast({ content: message });
    },
  });

  const startWorkflowMutation = useMutation({
    mutationFn: async () => {
      return api.post("/internal/v1/shared-item-invites/{invite_id}/start-workflow", {
        params: { path: { invite_id: inviteId! } },
      });
    },
    onSuccess: () => {
      toastModule.triggerToast({ content: "Invite email workflow started." });
    },
    onError: err => {
      const message = err instanceof Error ? err.message : "Failed to start workflow";
      toastModule.triggerToast({ content: message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return api.delete("/internal/v1/shared-item-invites/{invite_id}", {
        params: { path: { invite_id: inviteId! } },
      });
    },
    onSuccess: () => {
      toastModule.triggerToast({ content: "Invite deleted." });
      navigate(atlasRoutes.sharedItemInvites({}).path);
      invalidate();
    },
    onError: err => {
      const message = err instanceof Error ? err.message : "Failed to delete invite";
      toastModule.triggerToast({ content: message });
    },
  });

  const summaryRows = useMemo(() => {
    if (!invite) {
      return [];
    }
    return [
      { label: "Invite ID", value: invite.id },
      {
        label: "Shared Item Kind",
        value: SHARED_ITEM_KIND_LABELS[invite.shared_item_kind] ?? invite.shared_item_kind,
      },
      { label: "Shared Item ID", value: invite.shared_item_id },
      { label: "Invitee Email", value: invite.invitee_email },
      { label: "Normalized Email", value: invite.invitee_email_normalized },
      { label: "Verification Token", value: invite.verification_token },
      { label: "Inviter Space Account", value: invite.inviter_space_account_id },
      { label: "Accepted By", value: invite.accepted_by_space_account_id ?? "—" },
      { label: "Created", value: new Date(invite.created_at).toLocaleString() },
      { label: "Updated", value: new Date(invite.updated_at).toLocaleString() },
    ];
  }, [invite]);

  const sharedItemKindOptions = useMemo(() => {
    const base = [...SHARED_ITEM_KIND_SELECTABLE];
    if (!invite) {
      return base;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return base.includes(formState.sharedItemKind as any)
      ? base
      : [...base, formState.sharedItemKind];
  }, [formState.sharedItemKind, invite]);

  if (!inviteId) {
    return <div>Invalid invite id</div>;
  }

  if (result.isLoading) {
    return <div>Loading invite…</div>;
  }

  if (result.error || !invite) {
    return <div>Invite not found.</div>;
  }

  const handleUpdateSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload = {
      inviter_space_account_id: formState.inviterSpaceAccountId.trim() || undefined,
      shared_item_id: formState.sharedItemId.trim() || undefined,
      shared_item_kind: formState.sharedItemKind,
      invitee_email: formState.inviteeEmail.trim() || undefined,
      role: formState.role,
      expires_at: formState.expiresAt ? inputValueToIso(formState.expiresAt) : null,
      status: formState.status,
      accepted_by_space_account_id: formState.acceptedBySpaceAccountId.trim() || null,
      accepted_at: formState.acceptedAt ? inputValueToIso(formState.acceptedAt) : null,
      is_revoked: formState.isRevoked,
    };

    const normalizedInitial = {
      inviter_space_account_id: initialPayload.inviter_space_account_id,
      shared_item_id: initialPayload.shared_item_id,
      shared_item_kind: initialPayload.shared_item_kind,
      invitee_email: initialPayload.invitee_email,
      role: initialPayload.role,
      expires_at: initialPayload.expires_at,
      status: initialPayload.status,
      accepted_by_space_account_id: initialPayload.accepted_by_space_account_id,
      accepted_at: initialPayload.accepted_at,
      is_revoked: initialPayload.is_revoked,
    };

    const diff = computeDiff(payload, normalizedInitial);

    if (Object.keys(diff).length === 0) {
      toastModule.triggerToast({ content: "No changes to save." });
      return;
    }

    updateMutation.mutate(diff);
  };

  const handleStatusSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const status = form.status.value;
    const acceptedBy = form.acceptedBy.value.trim();
    const acceptedAtValue = form.acceptedAt.value;

    const body: Record<string, unknown> = {
      status,
    };

    if (acceptedBy) {
      body.accepted_by_space_account_id = acceptedBy;
    }
    if (acceptedAtValue) {
      const iso = inputValueToIso(acceptedAtValue);
      if (iso) {
        body.accepted_at = iso;
      }
    }

    markStatusMutation.mutate(body);
  };

  const handleUnrevoke = () => {
    updateMutation.mutate({ is_revoked: false });
  };

  const handleStartWorkflow = () => {
    if (confirm("Send invite email via workflow?")) {
      startWorkflowMutation.mutate();
    }
  };

  const openSharedItem = () => {
    if (invite.shared_item_kind === "NOTE") {
      navigate(atlasRoutes.notesView({ params: { noteId: invite.shared_item_id } }).path);
      return;
    }
    if (invite.shared_item_kind === "COLLECTION") {
      navigate(
        atlasRoutes.collectionsView({ params: { collectionId: invite.shared_item_id } }).path
      );
    }
  };

  return (
    <PageContainer>
      <Flex align="center" justify="between">
        <Box>
          <Title>Shared Item Invite</Title>
          <Flex align="center" gap="3">
            <Badge variant="soft">
              {SHARED_ITEM_INVITE_STATUS_LABELS[invite.status] ?? invite.status}
            </Badge>
            <Badge variant="soft" color={invite.is_revoked ? "red" : "green"}>
              {invite.is_revoked ? "Revoked" : "Active"}
            </Badge>
          </Flex>
        </Box>
        <Flex gap="2" wrap="wrap">
          {(invite.shared_item_kind === "NOTE" || invite.shared_item_kind === "COLLECTION") && (
            <Button variant="soft" onClick={openSharedItem}>
              Open Shared Item
            </Button>
          )}
          <Button
            variant="soft"
            onClick={handleStartWorkflow}
            disabled={startWorkflowMutation.isPending}
          >
            {startWorkflowMutation.isPending ? "Starting…" : "Send Invite Email"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigator.clipboard.writeText(invite.verification_token)}
          >
            Copy Token
          </Button>
        </Flex>
      </Flex>

      <Card>
        <Flex direction="column" gap="2">
          {summaryRows.map(row => (
            <SummaryRow key={row.label}>
              <SummaryLabel>{row.label}</SummaryLabel>
              <SummaryValue>{row.value}</SummaryValue>
            </SummaryRow>
          ))}
          <SummaryRow>
            <SummaryLabel>Expires At</SummaryLabel>
            <SummaryValue>
              {invite.expires_at ? new Date(invite.expires_at).toLocaleString() : "—"}
            </SummaryValue>
          </SummaryRow>
        </Flex>
      </Card>

      <Card>
        <SectionTitle>Update Invite</SectionTitle>
        <Form onSubmit={handleUpdateSubmit}>
          <Field>
            <Label>Inviter Space Account ID</Label>
            <Row>
              <Input
                value={formState.inviterSpaceAccountId}
                onChange={event =>
                  setFormState(prev => ({ ...prev, inviterSpaceAccountId: event.target.value }))
                }
              />
              <Button
                type="button"
                variant="soft"
                onClick={() =>
                  primarySpaceAccountId &&
                  setFormState(prev => ({ ...prev, inviterSpaceAccountId: primarySpaceAccountId }))
                }
                disabled={!primarySpaceAccountId || isPrimaryLoading}
              >
                Fill space account
              </Button>
            </Row>
          </Field>
          <Field>
            <Label>Shared Item ID</Label>
            <Input
              value={formState.sharedItemId}
              onChange={event =>
                setFormState(prev => ({ ...prev, sharedItemId: event.target.value }))
              }
            />
          </Field>
          <Field>
            <Label>Shared Item Kind</Label>
            <Select
              value={formState.sharedItemKind}
              onChange={event =>
                setFormState(prev => ({ ...prev, sharedItemKind: event.target.value }))
              }
            >
              {sharedItemKindOptions.map(kind => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Invitee Email</Label>
            <Input
              type="email"
              value={formState.inviteeEmail}
              onChange={event =>
                setFormState(prev => ({ ...prev, inviteeEmail: event.target.value }))
              }
            />
          </Field>
          <Field>
            <Label>Role</Label>
            <Select
              value={formState.role}
              onChange={event => setFormState(prev => ({ ...prev, role: event.target.value }))}
            >
              {ASSIGNABLE_ROLE_KINDS.map(role => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Expires At</Label>
            <Input
              type="datetime-local"
              value={formState.expiresAt}
              onChange={event => setFormState(prev => ({ ...prev, expiresAt: event.target.value }))}
            />
          </Field>
          <Field>
            <Label>Status</Label>
            <Select
              value={formState.status}
              onChange={event => setFormState(prev => ({ ...prev, status: event.target.value }))}
            >
              {SHARED_ITEM_INVITE_STATUSES.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Accepted By Space Account ID</Label>
            <Input
              value={formState.acceptedBySpaceAccountId}
              onChange={event =>
                setFormState(prev => ({ ...prev, acceptedBySpaceAccountId: event.target.value }))
              }
            />
          </Field>
          <Field>
            <Label>Accepted At</Label>
            <Input
              type="datetime-local"
              value={formState.acceptedAt}
              onChange={event =>
                setFormState(prev => ({ ...prev, acceptedAt: event.target.value }))
              }
            />
          </Field>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={formState.isRevoked}
              onChange={event =>
                setFormState(prev => ({ ...prev, isRevoked: event.target.checked }))
              }
            />
            <span>Mark as revoked</span>
          </CheckboxRow>

          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </Form>
      </Card>

      <Card>
        <SectionTitle>Update Status</SectionTitle>
        <Form onSubmit={handleStatusSubmit}>
          <Field>
            <Label>Status</Label>
            <Select name="status" defaultValue={invite.status}>
              {SHARED_ITEM_INVITE_STATUSES.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Accepted By Space Account ID</Label>
            <Input
              name="acceptedBy"
              placeholder="Optional"
              defaultValue={invite.accepted_by_space_account_id ?? ""}
            />
          </Field>
          <Field>
            <Label>Accepted At</Label>
            <Input
              name="acceptedAt"
              type="datetime-local"
              defaultValue={isoToLocalInputValue(invite.accepted_at)}
            />
          </Field>
          <Button type="submit" disabled={markStatusMutation.isPending}>
            {markStatusMutation.isPending ? "Updating…" : "Apply Status"}
          </Button>
        </Form>
      </Card>

      <Card>
        <SectionTitle>Danger Zone</SectionTitle>
        <Flex gap="2" wrap="wrap">
          <Button
            variant="soft"
            color="crimson"
            onClick={() => markRevokedMutation.mutate()}
            disabled={markRevokedMutation.isPending}
          >
            {markRevokedMutation.isPending ? "Marking…" : "Mark Revoked"}
          </Button>
          {invite.is_revoked && (
            <Button variant="soft" onClick={handleUnrevoke} disabled={updateMutation.isPending}>
              Unrevoke
            </Button>
          )}
          <Button
            color="red"
            onClick={() => {
              if (confirm("Delete this invite?")) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete Invite"}
          </Button>
        </Flex>
      </Card>

      <details>
        <summary>Raw JSON</summary>
        <RawJson>{JSON.stringify(invite, null, 2)}</RawJson>
      </details>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SummaryLabel = styled(Text)`
  font-size: 12px;
  color: var(--gray-10);
`;

const SummaryValue = styled(Text)`
  font-size: 14px;
  font-family: monospace;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
`;

const Label = styled.label`
  font-size: 14px;
  color: var(--gray-11);
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--gray-a5);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--gray-a5);
  border-radius: 6px;
  font-size: 14px;
  background: var(--color-surface);
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RawJson = styled.pre`
  background: var(--gray-a3);
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  font-size: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & > *:first-of-type {
    flex: 1;
  }
`;
